// Chief of Staff: the one place the admin talks to the AI team. A floating
// button opens a pop-up over any page; Chief answers in the team room and
// brings teammates in. Approvals show inside the chat with Approve / Reject.
import { useAuth } from '@/context/AuthContext'
import { useCeo } from '@/context/CeoContext'
import {
  type ApprovalItem,
  type ChatMessage,
  type ChatRoomSummary,
  createRoom,
  deleteRoom,
  fetchApprovals,
  fetchRoom,
  fetchRooms,
  sendMessage
} from '@/lib/ai-team'
import { notifyError, queryClient } from '@/lib/query'
import { cn } from '@/lib/utils'
import { AgentAvatar, ChiefOrb } from '@/pages/ai-team/AgentAvatar'
import { timeAgo } from '@/pages/ai-team/workflows/shared'
import { DecisionCard } from '@/pages/ai-team/workspace/parts'
import { useQuery } from '@tanstack/react-query'
import { ArrowUp, History, Loader2, Sparkles, SquarePen, Trash2, X } from 'lucide-react'
import { type FormEvent, type KeyboardEvent, useEffect, useRef, useState } from 'react'

const PAGE = 30
const SUGGESTIONS = ['What needs me today?', 'Brief me on what the team did yesterday', 'Which team spent the most this week?']

export function ChiefLauncher() {
  const { status } = useAuth()
  const { open, setOpen } = useCeo()
  const approvals = useQuery({
    queryKey: ['ai-approvals'],
    queryFn: fetchApprovals,
    refetchInterval: 15_000,
    enabled: status === 'authenticated'
  })
  const needs = approvals.data?.count ?? 0

  // ⌘J / Ctrl+J opens Chief from anywhere; Esc closes.
  useEffect(() => {
    function onKey(e: globalThis.KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault()
        setOpen(!open)
      } else if (e.key === 'Escape' && open) {
        // Something else already handled it, an IME is composing, or a dialog /
        // menu on top should close first.
        if (e.defaultPrevented || e.isComposing || e.keyCode === 229) return
        if (document.querySelector('[role="dialog"]:not([aria-label="Chief of Staff"]), [role="menu"]')) return
        setOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, setOpen])

  if (status !== 'authenticated') return null

  return (
    <>
      {open && <ChiefPopup onClose={() => setOpen(false)} />}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-label={open ? 'Close Chief of Staff' : 'Open Chief of Staff'}
        aria-expanded={open}
        className="fixed bottom-5 right-5 z-50 flex h-14 items-center gap-2.5 rounded-full bg-emerald-500 pl-3 pr-5 text-sm font-bold text-neutral-950 shadow-2xl shadow-emerald-900/40 transition-all hover:-translate-y-0.5 hover:bg-emerald-400 active:scale-95"
      >
        <span className="relative flex size-8 items-center justify-center rounded-full bg-neutral-950/15">
          {open ? <X className="size-4" /> : <Sparkles className="size-4" />}
          {!open && needs > 0 && (
            <span className="absolute -right-1.5 -top-1.5 flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold tabular-nums text-white">
              {needs > 99 ? '99+' : needs}
            </span>
          )}
        </span>
        <span className="hidden sm:inline">Chief of Staff</span>
      </button>
    </>
  )
}

const LAST_ROOM = 'chief-room'

function lastRoom(): number | null {
  try {
    return Number(localStorage.getItem(LAST_ROOM)) || null
  } catch {
    return null
  }
}

function ChiefPopup({ onClose }: { onClose: () => void }) {
  const rooms = useQuery({ queryKey: ['ai-rooms'], queryFn: fetchRooms, refetchInterval: 10_000 })
  const [chosen, setChosen] = useState<number | null>(lastRoom)
  const [view, setView] = useState<'chat' | 'list'>('chat')
  const [starting, setStarting] = useState(false)
  // A chat just started: show it even before the list refetch includes it.
  const [fresh, setFresh] = useState<ChatRoomSummary | null>(null)
  const listed = rooms.data ? [rooms.data.team, ...rooms.data.rooms] : []
  const all = fresh && rooms.data && !listed.some((r) => r.id === fresh.id) ? [...listed, fresh] : listed
  // The chat you were in, or the team room.
  const teamId = all.some((r) => r.id === chosen) ? chosen! : rooms.data?.team.id
  const current = all.find((r) => r.id === teamId)

  function open(id: number) {
    setChosen(id)
    setView('chat')
    setShown(PAGE)
    try {
      localStorage.setItem(LAST_ROOM, String(id))
    } catch {
      // fine: it just won't be remembered
    }
  }

  async function newChat() {
    if (starting) return
    setStarting(true)
    try {
      const room = await createRoom()
      setFresh(room)
      open(room.id)
      await queryClient.invalidateQueries({ queryKey: ['ai-rooms'] })
    } catch (err) {
      notifyError('Could not start a new chat', err)
    } finally {
      setStarting(false)
    }
  }
  const room = useQuery({
    queryKey: ['ai-room', teamId],
    queryFn: () => fetchRoom(teamId!),
    enabled: !!teamId,
    refetchInterval: (q) => (q.state.data?.working ? 1_500 : 6_000)
  })
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  // Set synchronously, so a double Enter / double click can't send twice.
  const sendingRef = useRef(false)
  const [shown, setShown] = useState(PAGE)
  const scroller = useRef<HTMLDivElement>(null)

  const visible = (room.data?.messages ?? []).filter(
    (m) => ((m.role === 'admin' || m.role === 'agent') && m.text.trim()) || m.card
  )
  const page = visible.slice(-shown)
  const lastId = visible.at(-1)?.id

  // Follow new messages.
  useEffect(() => {
    if (lastId) scroller.current?.scrollTo({ top: scroller.current.scrollHeight })
  }, [lastId])

  async function send(message: string) {
    if (!message.trim() || !teamId || sendingRef.current) return
    sendingRef.current = true
    setSending(true)
    try {
      await sendMessage(teamId, message.trim())
      setText('')
      await queryClient.invalidateQueries({ queryKey: ['ai-room', teamId] })
    } catch (err) {
      notifyError('Could not send that', err)
    } finally {
      sendingRef.current = false
      setSending(false)
    }
  }
  function onSubmit(e: FormEvent) {
    e.preventDefault()
    void send(text)
  }
  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    // Enter that confirms an IME composition (Japanese, Chinese…) isn't a send.
    if (e.nativeEvent.isComposing || e.keyCode === 229) return
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void send(text)
    }
  }

  return (
    <section
      role="dialog"
      aria-label="Chief of Staff"
      className="fixed bottom-24 right-5 z-50 flex h-[min(40rem,calc(100dvh-8rem))] w-[min(26rem,calc(100vw-2.5rem))] flex-col overflow-hidden rounded-[36px] border border-neutral-200 bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-200 dark:border-neutral-800 dark:bg-neutral-950"
    >
      <header className="flex items-center gap-3 border-b border-neutral-200 bg-gradient-to-r from-emerald-500/10 to-red-500/10 px-5 py-4 dark:border-neutral-800">
        <ChiefOrb state={room.data?.working ? 'working' : 'idle'} />
        <div className="min-w-0 flex-1">
          <p className="truncate font-bold text-neutral-900 dark:text-white">
            {view === 'list' ? 'Your chats' : current && current.kind !== 'team' ? current.title || 'Chat' : 'Chief of Staff'}
          </p>
          <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
            {view === 'list'
              ? `${all.length} ${all.length === 1 ? 'chat' : 'chats'}`
              : room.data?.working
                ? 'Working on it…'
                : current?.kind === 'team'
                  ? 'Team room · Chief answers and brings teammates in'
                  : 'Chief of Staff leads this chat'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setView(view === 'list' ? 'chat' : 'list')}
          aria-label={view === 'list' ? 'Back to the chat' : 'Your chats'}
          aria-pressed={view === 'list'}
          title="Your chats"
          className={cn(
            'inline-flex size-9 items-center justify-center rounded-full text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-white',
            view === 'list' && 'bg-neutral-100 text-neutral-900 dark:bg-neutral-800 dark:text-white'
          )}
        >
          <History className="size-4" />
        </button>
        <button
          type="button"
          onClick={newChat}
          disabled={starting}
          aria-label="New chat"
          title="New chat"
          className="inline-flex size-9 items-center justify-center rounded-full text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900 disabled:opacity-50 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-white"
        >
          {starting ? <Loader2 className="size-4 animate-spin" /> : <SquarePen className="size-4" />}
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="inline-flex size-9 items-center justify-center rounded-full text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-white"
        >
          <X className="size-4" />
        </button>
      </header>

      {view === 'list' ? (
        <ChatList
          rooms={all}
          currentId={teamId}
          onOpen={open}
          onNew={newChat}
          starting={starting}
          onRemoved={(id) => setFresh((f) => (f?.id === id ? null : f))}
        />
      ) : (
        <>
      <div ref={scroller} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {(rooms.isLoading || room.isLoading) &&
          [0, 1, 2].map((i) => (
            <div key={i} className={cn('h-14 animate-pulse rounded-[24px] bg-neutral-200 dark:bg-neutral-800', i % 2 ? 'ml-auto w-2/3' : 'w-3/4')} />
          ))}
        {(rooms.error || room.error) && (
          <p className="text-sm text-red-600 dark:text-red-400">Couldn't load the chat. Check your connection and reopen.</p>
        )}
        {visible.length > shown && (
          <button
            type="button"
            onClick={() => setShown((n) => n + PAGE)}
            className="mx-auto block rounded-full border border-neutral-200 px-4 py-1.5 text-xs font-semibold text-neutral-600 hover:bg-neutral-50 dark:border-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-900"
          >
            Show earlier ({visible.length - shown})
          </button>
        )}
        {room.data && visible.length === 0 && (
          <div className="space-y-3 pt-6 text-center">
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Try one of these:</p>
            <div className="flex flex-col items-center gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => send(s)}
                  disabled={sending || !teamId}
                  className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-500/20 disabled:opacity-50 dark:text-emerald-300"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {page.map((m) => (
          <Message key={m.id} message={m} />
        ))}
        {room.data?.working && (
          <p className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
            <Loader2 className="size-3.5 animate-spin" /> The team is working…
          </p>
        )}
      </div>

      <form onSubmit={onSubmit} className="flex items-end gap-2 border-t border-neutral-200 p-3 dark:border-neutral-800">
        <label htmlFor="chief-input" className="sr-only">
          Message Chief of Staff
        </label>
        <textarea
          id="chief-input"
          autoFocus
          rows={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          placeholder="Ask Chief of Staff…"
          className="max-h-32 min-h-11 flex-1 resize-none rounded-[24px] border border-neutral-200 bg-white px-4 py-2.5 text-sm text-neutral-900 outline-none placeholder:text-neutral-400 focus:border-emerald-500 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white dark:placeholder:text-neutral-500"
        />
        <button
          type="submit"
          disabled={sending || !text.trim() || !teamId}
          aria-label="Send"
          className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-neutral-950 transition-all hover:bg-emerald-400 active:scale-95 disabled:opacity-50"
        >
          {sending ? <Loader2 className="size-4 animate-spin" /> : <ArrowUp className="size-4" />}
        </button>
      </form>
        </>
      )}
    </section>
  )
}

function ChatList({
  rooms,
  currentId,
  onOpen,
  onNew,
  starting,
  onRemoved
}: {
  rooms: ChatRoomSummary[]
  currentId?: number
  onOpen: (id: number) => void
  onNew: () => void
  starting: boolean
  onRemoved: (id: number) => void
}) {
  const [q, setQ] = useState('')
  const [shown, setShown] = useState(PAGE)
  const [removing, setRemoving] = useState<number | null>(null)
  const list = rooms.filter((r) => !q.trim() || `${r.title} ${r.preview}`.toLowerCase().includes(q.trim().toLowerCase()))

  async function remove(r: ChatRoomSummary) {
    if (!window.confirm(`Delete "${r.title || 'this chat'}"?`)) return
    setRemoving(r.id)
    try {
      await deleteRoom(r.id)
      onRemoved(r.id)
      await queryClient.invalidateQueries({ queryKey: ['ai-rooms'] })
    } catch (err) {
      notifyError('Could not delete the chat', err)
    } finally {
      setRemoving(null)
    }
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex gap-2 border-b border-neutral-200 p-3 dark:border-neutral-800">
        <label className="sr-only" htmlFor="chief-search">
          Search chats
        </label>
        <input
          id="chief-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search chats"
          className="min-w-0 flex-1 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm text-neutral-900 outline-none placeholder:text-neutral-400 focus:border-emerald-500 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white dark:placeholder:text-neutral-500"
        />
        <button
          type="button"
          onClick={onNew}
          disabled={starting}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-sm font-bold text-neutral-950 hover:bg-emerald-400 disabled:opacity-50"
        >
          {starting ? <Loader2 className="size-4 animate-spin" /> : <SquarePen className="size-4" />} New
        </button>
      </div>
      <ul className="flex-1 space-y-1 overflow-y-auto p-2">
        {list.length === 0 && <li className="p-4 text-center text-sm text-neutral-500 dark:text-neutral-400">No chats match.</li>}
        {list.slice(0, shown).map((r) => (
          <li key={r.id} className="group flex items-center gap-1">
            <button
              type="button"
              onClick={() => onOpen(r.id)}
              aria-current={r.id === currentId ? 'true' : undefined}
              className={cn(
                'flex min-w-0 flex-1 items-center gap-3 rounded-[24px] px-3 py-2.5 text-left transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-900',
                r.id === currentId && 'bg-emerald-500/10'
              )}
            >
              {r.kind === 'direct' && r.agent ? (
                <AgentAvatar agentKey={r.agent.key} name={r.agent.name} size="sm" state={r.working ? 'working' : 'idle'} />
              ) : (
                <ChiefOrb state={r.working ? 'working' : 'idle'} />
              )}
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-neutral-900 dark:text-white">
                    {r.kind === 'team' ? 'Team room' : r.title || 'New chat'}
                  </span>
                  {r.waiting > 0 && (
                    <span className="shrink-0 rounded-full bg-amber-500 px-1.5 text-xs font-bold tabular-nums text-white">{r.waiting}</span>
                  )}
                  {r.working && <Loader2 className="size-3 shrink-0 animate-spin text-emerald-500" />}
                </span>
                <span className="block truncate text-xs text-neutral-500 dark:text-neutral-400">
                  {r.preview || 'No messages yet'} · {timeAgo(r.updated_at)}
                </span>
              </span>
            </button>
            {r.kind !== 'team' && (
              <button
                type="button"
                onClick={() => remove(r)}
                disabled={removing === r.id}
                aria-label={`Delete ${r.title || 'chat'}`}
                className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-neutral-400 opacity-100 transition-opacity hover:bg-neutral-100 hover:text-red-600 focus-visible:opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100 disabled:opacity-50 dark:hover:bg-neutral-800 dark:hover:text-red-400"
              >
                {removing === r.id ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
              </button>
            )}
          </li>
        ))}
        {list.length > shown && (
          <li>
            <button
              type="button"
              onClick={() => setShown((n) => n + PAGE)}
              className="mx-auto block rounded-full border border-neutral-200 px-4 py-1.5 text-xs font-semibold text-neutral-600 hover:bg-neutral-50 dark:border-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-900"
            >
              Show more ({list.length - shown})
            </button>
          </li>
        )}
      </ul>
    </div>
  )
}

function Message({ message: m }: { message: ChatMessage }) {
  if (m.card) {
    if (m.card.status !== 'open')
      return (
        <p className="text-center text-xs text-neutral-500 dark:text-neutral-400">
          {m.card.title} · <span className="font-semibold">{m.card.status_label}</span>
        </p>
      )
    const item: ApprovalItem = {
      ...m.card,
      agent: m.agent ?? { id: 0, key: 'chief_of_staff', name: 'Chief of Staff' },
      created_at: m.created_at,
      room: null,
      source: ''
    }
    return <DecisionCard entry={{ key: `c:${m.card.id}`, kind: 'one', item }} />
  }
  if (m.role === 'admin')
    return (
      <p className="ml-auto w-fit max-w-[85%] whitespace-pre-wrap rounded-[24px] bg-emerald-500 px-4 py-2.5 text-sm text-neutral-950">
        {m.text}
      </p>
    )
  return (
    <div className="flex max-w-[92%] items-start gap-2">
      <AgentAvatar agentKey={m.agent?.key} name={m.agent?.name} size="sm" />
      <div className="min-w-0">
        {m.agent && <p className="px-1 text-xs font-semibold text-neutral-500 dark:text-neutral-400">{m.agent.name}</p>}
        <p className="whitespace-pre-wrap rounded-[24px] bg-neutral-100 px-4 py-2.5 text-sm text-neutral-900 dark:bg-neutral-800 dark:text-white">
          {m.text}
        </p>
      </div>
    </div>
  )
}
